// frontend/src/components/cohesion/OnboardingTour.tsx
// Brighella v2 (ADR-014 D21) — first-login tour anchored to the ADR-013
// 3-noun model (Time, People, Place + Report).
//
// State: stored in users.ui_preferences.tour_dismissed_at via the existing
// /api/users/me/ui-preferences endpoint. Once set, the tour does not run
// again for this user. A manual replay is available from the Help page.
//
// Mount: once at the AppLayout root. The component shows nothing until
// the user-preferences load and confirm the tour has not been dismissed.
//
// UX-01/UX-02 (2026-06 UX audit):
// - Claims the highest-priority onboarding slot so the welcome tour always
//   runs BEFORE the profile-completion modal and any surface tour.
// - "Skip all tips" persists tours_disabled_at server-side (suppresses the
//   welcome tour AND every per-surface tour, across devices/logins).
// - Overlay is click-through until the tour dialog has rendered.

import { useCallback, useEffect, useMemo, useState } from 'react'
import Joyride, { STATUS, EVENTS } from 'react-joyride'
import type { CallBackProps, Step, TooltipRenderProps } from 'react-joyride'
import { useLanguage } from '../../i18n/index.jsx'
import api from '../../utils/api.js'
import TourTooltip from './TourTooltip'
import { useOnboardingSlot } from './onboardingOrchestrator'
import { mergeTourPrefsCache, readTourPrefsCache, writeTourPrefsCacheFromServer } from './tourPrefs'

interface OnboardingTourProps {
  forceRun?: boolean
  onClose?: () => void
}

function buildSteps(t: (key: string) => string): Step[] {
  return [
    {
      target: 'body',
      placement: 'center',
      title: t('tourWelcomeTitle'),
      content: t('tourWelcomeBody'),
      disableBeacon: true,
    },
    {
      target: 'a[href="/"]',
      title: t('tourDashboardTitle'),
      content: t('tourDashboardBody'),
    },
    {
      target: 'a[href="/calendar"]',
      title: t('tourCalendarTitle'),
      content: t('tourCalendarBody'),
    },
    {
      target: 'a[href="/productions"]',
      title: t('tourProductionsTitle'),
      content: t('tourProductionsBody'),
    },
    {
      target: 'a[href="/people"]',
      title: t('tourPeopleTitle'),
      content: t('tourPeopleBody'),
    },
    {
      target: '.sidebar-search input',
      title: t('tourSearchTitle'),
      content: t('tourSearchBody'),
    },
    {
      target: 'body',
      placement: 'center',
      title: t('tourDoneTitle'),
      content: t('tourDoneBody'),
      disableBeacon: true,
    },
  ]
}

export default function OnboardingTour({ forceRun = false, onClose }: OnboardingTourProps) {
  const { t } = useLanguage()
  const [shouldRun, setShouldRun] = useState(false)
  const [resolvedPrefs, setResolvedPrefs] = useState(false)
  // UX-02: overlay must never intercept clicks before the dialog renders.
  const [dialogReady, setDialogReady] = useState(false)

  // Decide whether to run on mount. If forceRun (manual replay), skip the
  // server check.
  useEffect(() => {
    if (forceRun) {
      setShouldRun(true)
      setResolvedPrefs(true)
      return
    }
    let cancelled = false
    // Fast-path suppress from the local cache while the GET is in flight.
    const cached = readTourPrefsCache()
    if (cached.tours_disabled_at || cached.tour_dismissed_at) setResolvedPrefs(true)
    api.get<Record<string, unknown>>('/users/me/ui-preferences')
      .then(({ data }) => {
        if (cancelled) return
        const prefs = (data || {}) as Record<string, unknown>
        writeTourPrefsCacheFromServer(prefs)            // server wins
        setShouldRun(!prefs.tours_disabled_at && !prefs.tour_dismissed_at)
        setResolvedPrefs(true)
      })
      .catch(() => {
        if (!cancelled) setResolvedPrefs(true)          // fail closed
      })
    return () => { cancelled = true }
  }, [forceRun])

  const steps = useMemo(() => buildSteps(t), [t])

  // UX-01: highest priority — the welcome tour goes first. Hold the slot
  // while prefs load so the profile modal cannot flash in before us.
  const wants = forceRun ? shouldRun : (!resolvedPrefs || shouldRun)
  const active = useOnboardingSlot('welcome-tour', wants, 3)

  const handleCallback = (data: CallBackProps) => {
    const { status, type } = data
    if (type === EVENTS.TOOLTIP) setDialogReady(true)
    const finished = status === STATUS.FINISHED || status === STATUS.SKIPPED
    const closedManually = type === EVENTS.TOUR_END
    if (finished || closedManually) {
      setShouldRun(false)
      setDialogReady(false)
      if (!forceRun) {
        const patch = { tour_dismissed_at: new Date().toISOString() }
        mergeTourPrefsCache(patch)
        api.put('/users/me/ui-preferences', patch)
          .catch(() => { /* best-effort */ })
      }
      if (onClose) onClose()
    }
  }

  // UX-02: "Skip all tips" — disable every auto-tour, server-side.
  const handleSkipAll = useCallback(() => {
    setShouldRun(false)
    setDialogReady(false)
    const now = new Date().toISOString()
    const patch = { tours_disabled_at: now, tour_dismissed_at: now }
    mergeTourPrefsCache(patch)
    api.put('/users/me/ui-preferences', patch).catch(() => { /* best-effort */ })
    if (onClose) onClose()
  }, [onClose])

  const tooltipComponent = useMemo(() => {
    return function OnboardingTourTooltip(props: TooltipRenderProps) {
      return <TourTooltip {...props} t={t} onSkipAll={handleSkipAll} />
    }
  }, [t, handleSkipAll])

  if (!active) return null
  if (!resolvedPrefs) return null
  if (!shouldRun) return null

  return (
    <Joyride
      steps={steps}
      run={shouldRun}
      continuous
      showProgress
      showSkipButton
      disableOverlayClose
      hideCloseButton={false}
      callback={handleCallback}
      tooltipComponent={tooltipComponent}
      locale={{
        back:  t('back'),
        close: t('close'),
        last:  t('tourFinish'),
        next:  t('next'),
        skip:  t('skip'),
      }}
      styles={{
        options: {
          primaryColor: '#4b3aa3',
          textColor: '#1f2329',
          backgroundColor: '#ffffff',
          arrowColor: '#ffffff',
          zIndex: 10000,
        },
        // Click-through until the dialog is on screen (UX-02 freeze fix).
        overlay: dialogReady ? {} : { pointerEvents: 'none' },
        tooltipContainer: { textAlign: 'left' },
      }}
    />
  )
}
